import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { motion } from "motion/react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import * as d3 from "d3";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Brain,
  CircleDot,
  CheckCircle2,
  Clipboard,
  Database,
  ExternalLink,
  FileText,
  FileUp,
  Filter,
  GitBranch,
  HeartPulse,
  ListChecks,
  Layers3,
  Network,
  Pin,
  Radio,
  Save,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Stethoscope,
  Table2,
  Target
} from "lucide-react";
import "./styles.css";
import {
  buildInsights,
  displaySubtitle,
  displayTitle,
  evidenceDebtCount,
  evidencePosture,
  generateBrief,
  healthcareImpact,
  impactScore,
  kindColors,
  kindLabels,
  loadJson,
  paths,
  recommendedActions,
  relatedRecords,
  sortedRecords,
  whoCares
} from "./lib/intelligence";
import { cn } from "./lib/utils";

const navItems = [
  ["Overview", "#overview", Activity],
  ["Assets", "#assets", Target],
  ["Graph", "#graph", Network],
  ["Impact", "#impact", HeartPulse],
  ["Records", "#records", Table2],
  ["Brief", "#brief", FileText],
  ["Help Others", "#help", ShieldCheck]
];

const filters = [
  ["all", "All"],
  ["vulnerability", "CVEs"],
  ["breach", "Breaches"],
  ["medical_device_advisory", "Devices"],
  ["package_advisory", "AI repo"],
  ["indicator", "IOCs"]
];

const aiLaunchSites = [
  ["ChatGPT", "OpenAI", "https://chatgpt.com/"],
  ["Claude", "Anthropic", "https://claude.ai/new"],
  ["Gemini", "Google", "https://gemini.google.com/app"],
  ["Perplexity", "Answer engine", "https://www.perplexity.ai/"],
  ["Copilot", "Microsoft", "https://copilot.microsoft.com/"],
  ["Grok", "xAI", "https://grok.com/"]
];

const sampleAssetInventory = `vendor,product,type,owner
Apache,Log4j,library,platform security
Progress,MOVEit Transfer,file transfer,claims operations
Palo Alto Networks,PAN-OS GlobalProtect,vpn,network security
Example Medical,Patient Monitor Gateway,medical device,clinical engineering
langchain,langchain,python package,healthcare AI
hospital.example.org,external domain,domain,security operations`;

const workflowSteps = ["Investigate", "Validate", "Escalate", "Share"];

const graphModes = [
  ["selected_path", "Selected path"],
  ["all_related", "All related"],
  ["source_provenance", "Source provenance"]
];

const briefModes = [
  ["executive", "Executive summary"],
  ["technical", "Technical triage"],
  ["leadership", "Hospital leadership"],
  ["vendor", "Vendor risk"],
  ["patient_safety", "Patient-safety impact"]
];

const briefTones = [
  ["neutral", "Neutral"],
  ["cautious", "Cautious"],
  ["urgent", "Urgent"],
  ["board_ready", "Board-ready"]
];

const savedViews = [
  ["critical_cves", "Critical CVEs", "KEV, high CVSS, high EPSS, or named healthcare-adjacent vulnerabilities."],
  ["device_risk", "Medical device risk", "Device advisories and patient-safety adjacent signals."],
  ["breach_claims", "Breach and ransomware", "OCR breaches, ransomware claims, and evidence that needs careful language."],
  ["ai_supply_chain", "AI supply chain", "AI repo/package risk, secrets, agents, RAG tools, and dependency exposure."],
  ["evidence_debt", "Evidence debt", "Records requiring careful claim language before public use."],
  ["patient_safety", "Patient safety", "Signals that can affect clinical continuity, medical workflows, or PHI operations."]
];

function readStoredJson(key, fallback) {
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function writeStoredJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage failures; the app remains fully usable without persistence.
  }
}

function readStoredText(key, fallback) {
  try {
    return window.localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

function writeStoredText(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures; the current session state still works.
  }
}

function App() {
  const [state, setState] = useState({
    records: [],
    graph: { nodes: [], edges: [] },
    simulations: [],
    devices: [],
    vulnerabilities: [],
    loading: true,
    error: null
  });
  const [selectedId, setSelectedId] = useState(null);
  const [filterKind, setFilterKind] = useState("all");
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState(false);
  const [workflow, setWorkflow] = useState(() => readStoredJson("atlas.workflow", {}));
  const [graphMode, setGraphMode] = useState(() => readStoredText("atlas.graphMode", "selected_path"));
  const [pinnedNodeId, setPinnedNodeId] = useState(() => readStoredText("atlas.pinnedNodeId", ""));
  const [selectedView, setSelectedView] = useState(() => readStoredText("atlas.savedView", "critical_cves"));
  const [briefMode, setBriefMode] = useState(() => readStoredText("atlas.briefMode", "technical"));
  const [briefTone, setBriefTone] = useState(() => readStoredText("atlas.briefTone", "cautious"));
  const [assetInput, setAssetInput] = useState(() => readStoredText("atlas.assetInventory", sampleAssetInventory));

  useEffect(() => {
    Promise.all([
      loadJson(paths.records),
      loadJson(paths.graph),
      loadJson(paths.simulations),
      loadJson(paths.devices),
      loadJson(paths.vulnerabilities)
    ])
      .then(([records, graph, simulations, devices, vulnerabilities]) => {
        setState({ records, graph, simulations, devices, vulnerabilities, loading: false, error: null });
        setSelectedId(sortedRecords(records)[0]?.id || null);
      })
      .catch((error) => setState((current) => ({ ...current, loading: false, error })));
  }, []);

  useEffect(() => writeStoredJson("atlas.workflow", workflow), [workflow]);
  useEffect(() => writeStoredText("atlas.graphMode", graphMode), [graphMode]);
  useEffect(() => writeStoredText("atlas.pinnedNodeId", pinnedNodeId), [pinnedNodeId]);
  useEffect(() => writeStoredText("atlas.savedView", selectedView), [selectedView]);
  useEffect(() => writeStoredText("atlas.briefMode", briefMode), [briefMode]);
  useEffect(() => writeStoredText("atlas.briefTone", briefTone), [briefTone]);
  useEffect(() => writeStoredText("atlas.assetInventory", assetInput), [assetInput]);

  const selected = useMemo(
    () => state.records.find((record) => record.id === selectedId) || sortedRecords(state.records)[0] || null,
    [selectedId, state.records]
  );

  const sorted = useMemo(() => sortedRecords(state.records), [state.records]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return sorted.filter((record) => {
      const kindMatch = filterKind === "all" || record.kind === filterKind;
      const viewMatch = selectedView === "all" || matchesSavedView(record, selectedView);
      if (!kindMatch) return false;
      if (!viewMatch) return false;
      if (!term) return true;
      const haystack = `${displayTitle(record)} ${record.title} ${record.summary} ${record.kind} ${(record.entities || []).join(" ")} ${JSON.stringify(record.attributes || {})}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [filterKind, search, selectedView, sorted]);

  const brief = useMemo(
    () => selected ? generateConfiguredBrief(selected, state.records, briefMode, briefTone) : "",
    [briefMode, briefTone, selected, state.records]
  );
  const assetMatches = useMemo(() => matchAssetsToRecords(assetInput, state.records), [assetInput, state.records]);

  function updateWorkflow(recordId, stage) {
    setWorkflow((current) => ({ ...current, [recordId]: stage }));
  }

  async function copyBrief() {
    if (!brief) return;
    try {
      await navigator.clipboard.writeText(brief);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  }

  if (state.loading) {
    return (
      <main className="grid min-h-screen place-items-center bg-[#071417] text-white">
        <div className="grid gap-3 text-center">
          <Sparkles className="mx-auto h-8 w-8 animate-pulse text-emerald-200" />
          <p className="text-sm uppercase tracking-[0.28em] text-emerald-100">Loading intelligence atlas</p>
        </div>
      </main>
    );
  }

  if (state.error) {
    return (
      <main className="grid min-h-screen place-items-center bg-[#071417] p-6 text-white">
        <div className="max-w-xl rounded-lg border border-red-300/25 bg-red-500/10 p-6">
          <h1 className="text-2xl font-black">Atlas failed to load</h1>
          <p className="mt-2 text-red-100">{state.error.message}</p>
        </div>
      </main>
    );
  }

  return (
    <Tooltip.Provider delayDuration={160}>
      <div className="cockpit min-h-screen bg-atlas text-slate-950">
        <TopNav records={state.records} />
        <main className="atlas-main">
          <section id="overview" className="cockpit-grid">
            <CommandDeck selected={selected} records={state.records} onFocus={() => selected && document.getElementById("impact")?.scrollIntoView({ behavior: "smooth" })} onCopy={copyBrief} copied={copied} />
            <OperationsPanel records={state.records} filtered={filtered} workflow={workflow} selectedView={selectedView} onView={setSelectedView} />
            <SignalWorkbench
              records={sorted}
              visibleRecords={filtered}
              selected={selected}
              selectedId={selected?.id}
              graph={state.graph}
              graphMode={graphMode}
              pinnedNodeId={pinnedNodeId}
              filterKind={filterKind}
              search={search}
              copied={copied}
              workflow={workflow}
              onFilter={setFilterKind}
              onSearch={setSearch}
              onSelect={setSelectedId}
              onCopy={copyBrief}
              onGraphMode={setGraphMode}
              onNodePin={setPinnedNodeId}
              onWorkflow={updateWorkflow}
            />
            <Metrics records={state.records} graph={state.graph} simulations={state.simulations} devices={state.devices} />
            <ImpactCompass records={state.records} simulations={state.simulations} />
            <InsightPanel records={state.records} graph={state.graph} simulations={state.simulations} />
          </section>

          <section id="assets" className="section-shell">
            <SectionHeader kicker="Exposure matcher" title="Find records that affect your environment">
              <button className="btn btn-glass" onClick={() => setAssetInput(sampleAssetInventory)}>
                <Clipboard className="h-4 w-4" />
                Load sample
              </button>
            </SectionHeader>
            <AssetMatcher assetInput={assetInput} matches={assetMatches} onAssetInput={setAssetInput} onSelect={setSelectedId} />
          </section>

          <section id="graph" className="section-shell">
            <SectionHeader kicker="Relationship explorer" title="Find the intersections that change priority">
              <div className="header-actions">
                <GraphModeTabs graphMode={graphMode} onChange={setGraphMode} />
                <FilterBar filterKind={filterKind} onChange={setFilterKind} />
              </div>
            </SectionHeader>
            <RelationshipExplorer graph={state.graph} records={state.records} filterKind={filterKind} selectedId={selected?.id} pinnedNodeId={pinnedNodeId} onSelect={setSelectedId} onNodePin={setPinnedNodeId} graphMode={graphMode} />
          </section>

          <section id="impact" className="section-shell">
            <SectionHeader kicker="Impact translator" title="Turn technical signals into decisions" />
            {selected && <ImpactTranslator record={selected} records={state.records} workflow={workflow[selected.id] || workflowSteps[0]} onWorkflow={(stage) => updateWorkflow(selected.id, stage)} />}
          </section>

          <section id="records" className="section-shell">
            <SectionHeader kicker="Evidence ledger" title="Search, filter, verify">
              <label className="relative block w-full min-w-0 max-w-md">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  className="h-11 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm outline-none transition focus:border-teal-600 focus:ring-4 focus:ring-teal-600/15"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="MOVEit, patient monitor, ransomware, AI repo"
                  type="search"
                />
              </label>
            </SectionHeader>
            <SavedViews selectedView={selectedView} onView={setSelectedView} records={state.records} />
            <EvidenceStats records={state.records} filtered={filtered} />
            <div className="evidence-layout">
              <RecordsTable records={filtered} selectedId={selected?.id} onSelect={setSelectedId} workflow={workflow} onWorkflow={updateWorkflow} />
              <RecordDetail record={selected} records={state.records} workflow={selected ? workflow[selected.id] || workflowSteps[0] : workflowSteps[0]} onWorkflow={(stage) => selected && updateWorkflow(selected.id, stage)} />
            </div>
          </section>

          <section id="brief" className="section-shell">
            <SectionHeader kicker="Brief builder" title="Generate useful, non-alarmist output">
              <BriefControls briefMode={briefMode} briefTone={briefTone} onMode={setBriefMode} onTone={setBriefTone} onCopy={copyBrief} copied={copied} />
            </SectionHeader>
            <div className="brief-builder">
              <div className="brief-editor">
                <div className="brief-editor-head">
                  <div>
                    <span>Current prompt</span>
                    <strong>{selected ? displayTitle(selected) : "No signal selected"}</strong>
                  </div>
                  <button className="btn btn-glass" onClick={copyBrief}>
                    <Clipboard className="h-4 w-4" />
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <textarea className="brief-output" value={brief} readOnly />
              </div>
              <aside className="brief-side">
                <div className="ai-launch-card">
                  <div className="brief-card-head">
                    <span>Send to AI</span>
                    <strong>Copy the brief, then open a workspace</strong>
                  </div>
                  <div className="ai-launch-grid">
                    {aiLaunchSites.map(([name, owner, url]) => (
                      <a key={name} className="ai-launch-button" href={url} target="_blank" rel="noreferrer" onClick={copyBrief}>
                        <span>
                          <strong>{name}</strong>
                          <small>{owner}</small>
                        </span>
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    ))}
                  </div>
                </div>
                <div className="brief-use-card">
                  <div className="brief-card-head">
                    <span>Best use</span>
                    <strong>Operational and evidence-aware output</strong>
                  </div>
                  <ul>
                    <li>Hospital security weekly notes</li>
                    <li>Vendor-risk conversations</li>
                    <li>Medical-device cyber watch</li>
                    <li>AI security portfolio writeups</li>
                  </ul>
                </div>
                <div className="brief-note">
                  <strong>Workflow</strong>
                  <p>These links copy the current brief first, then open the AI workspace in a new tab so you can paste and refine without losing source context.</p>
                </div>
              </aside>
            </div>
          </section>

          <HelpSection />
        </main>
      </div>
    </Tooltip.Provider>
  );
}

function SideRail({ records }) {
  return (
    <aside className="atlas-rail">
      <a className="flex items-center gap-3 text-white no-underline" href="#overview">
        <span className="grid h-12 w-12 place-items-center rounded-lg border border-white/40 bg-emerald-100 text-sm font-black text-[#092c33] shadow-2xl shadow-black/20">HC</span>
        <span>
          <strong className="block leading-tight">Healthcare Cyber</strong>
          <small className="block text-sm text-teal-100/75">Intelligence Atlas</small>
        </span>
      </a>
      <nav className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
        {navItems.map(([label, href, Icon]) => (
          <a key={href} className="group flex items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-teal-50/90 transition hover:border-white/10 hover:bg-white/10" href={href}>
            <Icon className="h-4 w-4 text-teal-200/75 transition group-hover:text-teal-100" />
            <span>{label}</span>
          </a>
        ))}
      </nav>
      <div className="mt-auto rounded-lg border border-white/15 bg-white/8 p-4 shadow-inner">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-teal-100/70">Evidence posture</p>
        <strong className="mt-2 block leading-snug text-white">{evidencePosture(records)}</strong>
      </div>
    </aside>
  );
}

function TopNav({ records }) {
  return (
    <header className="top-command">
      <a className="flex min-w-0 items-center gap-3 text-white no-underline" href="#overview">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-cyan-200/40 bg-cyan-100 text-sm font-black text-[#061a1f] shadow-lg shadow-cyan-500/15">HC</span>
        <span className="min-w-0">
          <strong className="block leading-tight">Healthcare Cyber</strong>
          <small className="block text-xs text-cyan-100/65">Intelligence Atlas</small>
        </span>
      </a>
      <nav className="top-command-nav">
        {navItems.map(([label, href, Icon]) => (
          <a key={href} className="top-command-link" href={href}>
            <Icon className="h-4 w-4" />
            <span>{label}</span>
          </a>
        ))}
      </nav>
      <div className="top-command-posture">
        <span>Evidence posture</span>
        <strong>{evidencePosture(records)}</strong>
      </div>
    </header>
  );
}

function TopStatus({ records }) {
  return (
    <section className="sticky top-5 z-20 grid gap-3 rounded-lg border border-slate-200/90 bg-white/85 p-3 shadow-xl shadow-slate-900/8 backdrop-blur md:grid-cols-2">
      <div className="flex min-w-0 items-center gap-3">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-emerald-100">
          <CircleDot className="h-4 w-4 text-emerald-700" />
        </span>
        <div className="min-w-0">
          <strong className="block text-sm text-[#092c33]">Static intelligence site</strong>
          <span className="block text-sm text-slate-600">normalized healthcare OSINT records</span>
        </div>
      </div>
      <div className="min-w-0 md:text-right">
        <strong className="block text-sm text-[#092c33]">{evidencePosture(records)}</strong>
        <span className="block text-sm text-slate-600">claim language is shown before action</span>
      </div>
    </section>
  );
}

function Hero({ selected, onFocus, onCopy, copied }) {
  return (
    <motion.article initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="hero-card xl:row-span-2">
      <div>
        <p className="kicker text-teal-100">Operational intelligence</p>
        <h1 className="mt-4 max-w-3xl text-5xl font-black leading-[0.95] tracking-normal text-white sm:text-6xl 2xl:text-7xl">Healthcare Cyber Intelligence Atlas</h1>
        <p className="mt-6 max-w-2xl text-base leading-7 text-teal-50/86 sm:text-lg">
          Connect vulnerabilities, breach evidence, ransomware claims, medical-device cyber, AI repo risk, detections, simulations, and source confidence into decisions people can actually use.
        </p>
      </div>
      <div className="grid gap-4">
        <div className="mission-grid grid grid-cols-2 gap-2 md:grid-cols-4">
          {["Evidence-aware", "Clinical impact", "Graph connected", "Brief ready"].map((item) => (
            <span key={item} className="grid min-h-11 place-items-center rounded-lg border border-white/16 bg-white/8 px-2 text-center text-xs font-black text-teal-50">
              {item}
            </span>
          ))}
        </div>
        <div className="grid gap-2 sm:flex">
          <button className="btn btn-primary" onClick={onFocus}>
            Focus highest impact
            <ArrowRight className="h-4 w-4" />
          </button>
          <button className="btn btn-glass" onClick={onCopy}>
            {copied ? "Brief copied" : "Copy current brief"}
          </button>
        </div>
        {selected && (
          <div className="rounded-lg border border-white/12 bg-black/16 p-3 text-sm text-teal-50/80">
            Current focus: <strong className="text-white">{selected.title}</strong>
          </div>
        )}
      </div>
    </motion.article>
  );
}

function CommandDeck({ selected, records, onFocus, onCopy, copied }) {
  return (
    <motion.article initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="command-deck">
      <div className="command-deck-main">
        <p className="kicker">Healthcare threat operations</p>
        <h1>Cyber intelligence that explains clinical impact.</h1>
        <p>
          A working atlas for vulnerabilities, ransomware claims, medical-device advisories, AI repository risk, detections, simulations, and source confidence.
        </p>
        <div className="command-actions">
          <button className="btn btn-primary" onClick={onFocus}>
            Focus highest impact
            <ArrowRight className="h-4 w-4" />
          </button>
          <button className="btn btn-glass" onClick={onCopy}>{copied ? "Brief copied" : "Copy current brief"}</button>
        </div>
      </div>
      <div className="command-deck-side">
        <div>
          <span>Current signal</span>
          <strong>{selected ? displayTitle(selected) : "No signal selected"}</strong>
        </div>
        <div>
          <span>Evidence posture</span>
          <strong>{evidencePosture(records)}</strong>
        </div>
        <div>
          <span>Decision model</span>
          <strong>Clinical, technical, and source confidence together</strong>
        </div>
      </div>
    </motion.article>
  );
}

function OperationsPanel({ records, filtered, workflow, selectedView, onView }) {
  const readiness = liveDataReadiness(records);
  const stageCounts = workflowSteps.map((stage) => [stage, records.filter((record) => (workflow[record.id] || workflowSteps[0]) === stage).length]);
  return (
    <section className="ops-panel">
      <div className="ops-card ops-card-wide">
        <div className="ops-card-head">
          <Radio className="h-4 w-4" />
          <span>Live-data readiness</span>
        </div>
        <strong>{readiness.status}</strong>
        <p>{readiness.summary}</p>
        <div className="readiness-grid">
          {readiness.items.map(([label, value]) => (
            <div key={label}>
              <span>{label}</span>
              <b>{value}</b>
            </div>
          ))}
        </div>
      </div>
      <div className="ops-card">
        <div className="ops-card-head">
          <Save className="h-4 w-4" />
          <span>Saved view</span>
        </div>
        <strong>{savedViews.find(([id]) => id === selectedView)?.[1] || "All records"}</strong>
        <p>{filtered.length} records currently visible. View choice is saved locally.</p>
        <select value={selectedView} onChange={(event) => onView(event.target.value)}>
          <option value="all">All records</option>
          {savedViews.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
      </div>
      <div className="ops-card">
        <div className="ops-card-head">
          <CheckCircle2 className="h-4 w-4" />
          <span>Workflow state</span>
        </div>
        <div className="workflow-mini-grid">
          {stageCounts.map(([stage, count]) => (
            <div key={stage}>
              <b>{count}</b>
              <span>{stage}</span>
            </div>
          ))}
        </div>
        <p>Per-record analyst state is persisted in this browser.</p>
      </div>
    </section>
  );
}

function Metrics({ records, graph, simulations, devices }) {
  const items = [
    ["Records", records.length, Database],
    ["Graph nodes", graph.nodes.length, Network],
    ["Graph edges", graph.edges.length, GitBranch],
    ["Simulations", simulations.length, Brain],
    ["Device dossiers", devices.length, Stethoscope],
    ["Verification debt", evidenceDebtCount(records), AlertTriangle]
  ];
  return (
    <div className="grid grid-cols-2 gap-3">
      {items.map(([label, value, Icon]) => (
        <motion.article key={label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="metric-card">
          <Icon className="h-5 w-5 text-teal-700" />
          <b className="text-4xl font-black text-slate-950">{value}</b>
          <span className="text-xs font-black uppercase tracking-[0.12em] text-slate-500">{label}</span>
        </motion.article>
      ))}
    </div>
  );
}

function SignalWorkbench({ records, visibleRecords, selected, selectedId, graph, graphMode, pinnedNodeId, filterKind, search, copied, workflow, onFilter, onSearch, onSelect, onCopy, onGraphMode, onNodePin, onWorkflow }) {
  return (
    <section className="signal-workbench">
      <div className="workbench-head">
        <div>
          <p className="kicker">Signal workspace</p>
          <h2>Click a vulnerability, breach, device advisory, IOC, or AI repo risk and trace what matters.</h2>
        </div>
        <div className="workbench-actions">
          <GraphModeTabs graphMode={graphMode} onChange={onGraphMode} />
          <FilterBar filterKind={filterKind} onChange={onFilter} />
        </div>
      </div>
      <div className="workbench-grid">
        <SignalQueue records={visibleRecords.length ? visibleRecords : records.slice(0, 10)} selectedId={selectedId} search={search} workflow={workflow} onSearch={onSearch} onSelect={onSelect} />
        <RelationshipMap graph={graph} records={records} filterKind={filterKind} selectedId={selectedId} graphMode={graphMode} pinnedNodeId={pinnedNodeId} onSelect={onSelect} onNodePin={onNodePin} />
        <SelectedSignalPanel record={selected} records={records} copied={copied} workflow={selected ? workflow[selected.id] || workflowSteps[0] : workflowSteps[0]} onCopy={onCopy} onWorkflow={(stage) => selected && onWorkflow(selected.id, stage)} />
      </div>
    </section>
  );
}

function SignalQueue({ records, selectedId, search, workflow, onSearch, onSelect }) {
  return (
    <aside className="signal-queue">
      <div className="queue-search">
        <Search className="h-4 w-4" />
        <input value={search} onChange={(event) => onSearch(event.target.value)} placeholder="Filter signals" />
      </div>
      <div className="signal-list">
        {records.slice(0, 14).map((record) => (
          <button key={record.id} className={cn("signal-row", selectedId === record.id && "signal-row-active")} onClick={() => onSelect(record.id)}>
            <span className="signal-row-main">
              <strong>{displayTitle(record)}</strong>
              <span>{displaySubtitle(record)} / {record.evidence?.claim_language || "unknown"}</span>
              <em>{workflow[record.id] || workflowSteps[0]}</em>
            </span>
            <Badge kind={record.kind}>{impactScore(record)}</Badge>
          </button>
        ))}
      </div>
    </aside>
  );
}

function RelationshipMap({ graph, records, filterKind, selectedId, graphMode, pinnedNodeId, onSelect, onNodePin }) {
  return (
    <section className="relationship-panel">
      <div className="relationship-toolbar">
        <div>
          <p className="kicker">Relationship explorer</p>
          <h3>Readable intersections, not tangled lines</h3>
        </div>
      </div>
      <RelationshipExplorer graph={graph} records={records} filterKind={filterKind} selectedId={selectedId} pinnedNodeId={pinnedNodeId} onSelect={onSelect} onNodePin={onNodePin} graphMode={graphMode} compact />
    </section>
  );
}

function GraphModeTabs({ graphMode, onChange }) {
  return (
    <div className="graph-mode-tabs" aria-label="Relationship map mode">
      {graphModes.map(([id, label]) => (
        <button key={id} className={cn(graphMode === id && "graph-mode-active")} onClick={() => onChange(id)}>
          {id === "selected_path" && <Pin className="h-3.5 w-3.5" />}
          {id === "all_related" && <Layers3 className="h-3.5 w-3.5" />}
          {id === "source_provenance" && <SlidersHorizontal className="h-3.5 w-3.5" />}
          {label}
        </button>
      ))}
    </div>
  );
}

function RelationshipExplorer({ graph, records, filterKind, selectedId, graphMode, pinnedNodeId, onSelect, onNodePin, compact = false }) {
  const model = useMemo(
    () => buildRelationshipExplorer(graph, records, selectedId, filterKind, graphMode),
    [graph, records, selectedId, filterKind, graphMode]
  );
  if (!model.selectedRecord) {
    return (
      <div className="relationship-empty">
        <Network className="h-5 w-5" />
        <strong>No relationship data available</strong>
        <span>Select a record to inspect connected entities, evidence, and source overlap.</span>
      </div>
    );
  }
  return (
    <div className={cn("relationship-explorer", compact && "relationship-explorer-compact")}>
      <div className="relationship-summary">
        <div>
          <Badge kind={model.selectedRecord.kind}>{kindLabels[model.selectedRecord.kind] || model.selectedRecord.kind}</Badge>
          <h3>{displayTitle(model.selectedRecord)}</h3>
          <p>{model.modeCopy}</p>
        </div>
        <div className="relationship-summary-score">
          <span>Impact</span>
          <strong>{impactScore(model.selectedRecord)}</strong>
          <small>{model.intersections.length} intersections</small>
        </div>
      </div>
      <RelationshipPathColumns model={model} pinnedNodeId={pinnedNodeId} onSelect={onSelect} onNodePin={onNodePin} />
      <div className="relationship-analysis-grid">
        <IntersectionMatrix intersections={model.intersections} selectedRecord={model.selectedRecord} onSelect={onSelect} compact={compact} />
        <ProvenancePanel model={model} />
      </div>
    </div>
  );
}

function RelationshipPathColumns({ model, pinnedNodeId, onSelect, onNodePin }) {
  return (
    <div className="relationship-path-grid" aria-label="Selected relationship lanes">
      {model.columns.map((column) => (
        <section key={column.kind} className="relationship-lane">
          <div className="relationship-lane-head">
            <span>{column.label}</span>
            <b>{column.total}</b>
          </div>
          <div className="relationship-lane-stack">
            {column.nodes.length ? column.nodes.map((node) => (
              <RelationshipNodeCard key={node.id} node={node} pinned={pinnedNodeId === node.id} onSelect={onSelect} onNodePin={onNodePin} />
            )) : <p className="relationship-lane-empty">No direct match</p>}
          </div>
        </section>
      ))}
    </div>
  );
}

function RelationshipNodeCard({ node, pinned, onSelect, onNodePin }) {
  const card = (
    <>
      <span>{node.kindLabel}</span>
      <strong>{node.label}</strong>
      {node.detail && <small>{node.detail}</small>}
    </>
  );
  const className = cn("relationship-node-card", node.active && "relationship-node-active", pinned && "relationship-node-pinned", node.clickable && "relationship-node-clickable");
  if (node.clickable) {
    return (
      <button className={className} onClick={() => {
        onNodePin?.(node.id);
        onSelect?.(node.id);
      }}>
        {card}
      </button>
    );
  }
  return (
    <article className={className} onClick={() => onNodePin?.(node.id)}>
      {card}
    </article>
  );
}

function IntersectionMatrix({ intersections, selectedRecord, onSelect, compact }) {
  const rows = compact ? intersections.slice(0, 5) : intersections.slice(0, 10);
  return (
    <section className="intersection-panel">
      <div className="relationship-section-head">
        <div>
          <span>Intersection matrix</span>
          <strong>Records sharing the same context</strong>
        </div>
        <b>{intersections.length}</b>
      </div>
      <div className="intersection-list">
        {rows.length ? rows.map((item) => (
          <button key={item.record.id} className="intersection-row" onClick={() => onSelect?.(item.record.id)}>
            <div className="intersection-row-main">
              <div>
                <Badge kind={item.record.kind}>{kindLabels[item.record.kind] || item.record.kind}</Badge>
                <strong>{displayTitle(item.record)}</strong>
              </div>
              <p>{item.reason}</p>
              <div className="intersection-shared">
                {item.shared.slice(0, 5).map((node) => <span key={node.id}>{node.label}</span>)}
              </div>
            </div>
            <div className="intersection-score">
              <span>{item.score}</span>
              <i style={{ "--score": `${item.score}%` }} />
            </div>
          </button>
        )) : (
          <div className="intersection-empty">
            <strong>No strong intersections for {displayTitle(selectedRecord)}</strong>
            <span>Try All related mode or select a record with shared CVEs, entities, indicators, or sources.</span>
          </div>
        )}
      </div>
    </section>
  );
}

function ProvenancePanel({ model }) {
  const refs = model.provenance.references.slice(0, 4);
  return (
    <aside className="provenance-panel">
      <div className="relationship-section-head">
        <div>
          <span>Provenance</span>
          <strong>Why this can be trusted</strong>
        </div>
        <ShieldCheck className="h-4 w-4" />
      </div>
      <div className="provenance-grid">
        <InfoLine label="Claim language" value={model.provenance.claimLanguage} />
        <InfoLine label="Evidence source" value={model.provenance.evidenceSource} />
        <InfoLine label="Retrieved" value={model.provenance.retrievedAt} />
        <InfoLine label="Direct sources" value={model.provenance.sourceNodes.map((node) => node.label).join(", ") || "No source node"} />
      </div>
      <div className="provenance-meaning">
        <strong>Relationship meaning</strong>
        <p>{model.provenance.meaning}</p>
      </div>
      <div className="provenance-links">
        {refs.length ? refs.map((url) => (
          <a key={url} href={url} target="_blank" rel="noreferrer">
            <span>{shortUrl(url)}</span>
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )) : <span>No external references included in this record.</span>}
      </div>
    </aside>
  );
}

function buildRelationshipExplorer(graph, records, selectedId, filterKind, graphMode) {
  const safeGraph = graph || { nodes: [], edges: [] };
  const nodeById = new Map((safeGraph.nodes || []).map((node) => [node.id, node]));
  const recordById = new Map(records.map((record) => [record.id, record]));
  const visibleRecords = records.filter((record) => filterKind === "all" || record.kind === filterKind);
  const selectedRecord = recordById.get(selectedId) || visibleRecords[0] || records[0];
  const resolvedSelectedId = selectedRecord?.id;
  const selectedNode = resolvedSelectedId ? nodeById.get(resolvedSelectedId) || recordToNode(selectedRecord) : null;
  const selectedNeighborIds = resolvedSelectedId ? recordNeighborIds(safeGraph, resolvedSelectedId) : new Set();
  const selectedNeighbors = [...selectedNeighborIds].map((id) => nodeById.get(id)).filter(Boolean);
  const selectedSources = selectedNeighbors.filter((node) => node.kind === "source");
  const selectedSourceLabels = new Set([
    ...selectedSources.map((node) => normalizeTerm(node.label)),
    normalizeTerm(selectedRecord?.source),
    normalizeTerm(selectedRecord?.evidence?.source)
  ].filter(Boolean));
  const columns = relationshipColumns(selectedNode, selectedNeighbors, selectedRecord, records, graphMode);
  const intersections = visibleRecords
    .filter((record) => record.id !== resolvedSelectedId)
    .map((record) => scoreRecordIntersection(record, selectedRecord, selectedNeighborIds, selectedSourceLabels, safeGraph, nodeById))
    .filter((item) => item.score > 0)
    .filter((item) => graphMode !== "source_provenance" || item.shared.some((node) => node.kind === "source") || sameEvidenceSource(item.record, selectedRecord))
    .sort((a, b) => b.score - a.score || impactScore(b.record) - impactScore(a.record));
  if (graphMode === "all_related") {
    const related = relatedRecords(selectedRecord, records)
      .filter((record) => record.id !== resolvedSelectedId && !intersections.some((item) => item.record.id === record.id))
      .slice(0, 4)
      .map((record) => fallbackIntersection(record, selectedRecord));
    intersections.push(...related);
  }
  return {
    selectedRecord,
    selectedNode,
    columns,
    intersections,
    modeCopy: relationshipModeCopy(graphMode),
    provenance: {
      claimLanguage: selectedRecord?.evidence?.claim_language || "unknown",
      evidenceSource: selectedRecord?.evidence?.source || selectedRecord?.source || "unknown",
      retrievedAt: (selectedRecord?.evidence?.retrieved_at || selectedRecord?.created_at || "unknown").replace("T", " ").slice(0, 16),
      sourceNodes: selectedSources,
      references: selectedRecord?.references || [],
      meaning: relationshipMeaning(selectedRecord, intersections, selectedNeighbors)
    }
  };
}

function relationshipColumns(selectedNode, selectedNeighbors, selectedRecord, records, graphMode) {
  const lanes = [
    ["record", "Signal"],
    ["entity", "Entities"],
    ["vulnerability", "CVEs"],
    ["observable", "Indicators"],
    ["source", "Sources"]
  ];
  const grouped = Object.fromEntries(lanes.map(([kind]) => [kind, []]));
  if (selectedNode) grouped.record.push(selectedNode);
  selectedNeighbors.forEach((node) => {
    const lane = grouped[node.kind] ? node.kind : "entity";
    grouped[lane].push(node);
  });
  if (graphMode === "all_related" && selectedRecord) {
    relatedRecords(selectedRecord, records).slice(0, 3).forEach((record) => grouped.record.push(recordToNode(record)));
  }
  return lanes.map(([kind, label]) => {
    const unique = dedupeNodes(grouped[kind]).sort((a, b) => {
      if (a.id === selectedRecord?.id) return -1;
      if (b.id === selectedRecord?.id) return 1;
      return displayRelationshipNode(a, records).label.localeCompare(displayRelationshipNode(b, records).label);
    });
    return {
      kind,
      label,
      total: unique.length,
      nodes: unique.slice(0, kind === "record" ? 4 : 6).map((node) => displayRelationshipNode(node, records, selectedRecord?.id))
    };
  });
}

function scoreRecordIntersection(record, selectedRecord, selectedNeighborIds, selectedSourceLabels, graph, nodeById) {
  const neighborIds = recordNeighborIds(graph, record.id);
  const shared = [...neighborIds]
    .filter((id) => selectedNeighborIds.has(id))
    .map((id) => nodeById.get(id))
    .filter(Boolean);
  const evidenceShared = sameEvidenceSource(record, selectedRecord);
  let score = shared.reduce((total, node) => total + relationshipWeight(node.kind), 0);
  if (evidenceShared) score += 12;
  if (sameRecordText(record, selectedRecord)) score += 10;
  if (shared.length) score += Math.min(18, impactScore(record) / 5);
  const sourceMatched = shared.some((node) => node.kind === "source" && selectedSourceLabels.has(normalizeTerm(node.label)));
  if (sourceMatched) score += 10;
  const topShared = shared.sort((a, b) => relationshipWeight(b.kind) - relationshipWeight(a.kind)).slice(0, 8);
  return {
    record,
    score: Math.min(100, Math.round(score)),
    shared: topShared,
    reason: intersectionReason(topShared, evidenceShared)
  };
}

function fallbackIntersection(record, selectedRecord) {
  return {
    record,
    score: Math.min(64, 28 + Math.round(impactScore(record) / 4)),
    shared: sharedRecordTerms(record, selectedRecord).map((term, index) => ({ id: `${record.id}-${term}-${index}`, label: term, kind: "entity" })),
    reason: "Related by record-level entities, vulnerabilities, or healthcare context."
  };
}

function recordNeighborIds(graph, recordId) {
  const ids = new Set();
  (graph.edges || []).forEach((edge) => {
    if (edge.source === recordId) ids.add(edge.target);
    if (edge.target === recordId) ids.add(edge.source);
  });
  return ids;
}

function displayRelationshipNode(node, records, selectedId) {
  const record = records.find((item) => item.id === node.id);
  const label = record ? displayTitle(record) : node.label;
  return {
    id: node.id,
    label,
    active: selectedId === node.id,
    clickable: Boolean(record),
    kind: record?.kind || node.kind,
    kindLabel: record ? kindLabels[record.kind] || record.kind : nodeKindLabel(node.kind),
    detail: record ? `${impactScore(record)}/100 impact` : node.attributes?.record_kind || node.kind
  };
}

function recordToNode(record) {
  return { id: record.id, kind: "record", label: displayTitle(record), attributes: { record_kind: record.kind } };
}

function dedupeNodes(nodes) {
  return [...new Map(nodes.filter(Boolean).map((node) => [node.id, node])).values()];
}

function relationshipWeight(kind) {
  return { vulnerability: 34, observable: 30, entity: 24, source: 16, record: 10 }[kind] || 14;
}

function nodeKindLabel(kind) {
  return { record: "Signal", entity: "Entity", vulnerability: "CVE", observable: "Indicator", source: "Source" }[kind] || kind;
}

function relationshipModeCopy(graphMode) {
  if (graphMode === "all_related") return "Expands beyond the selected path to show records that share CVEs, entities, indicators, sources, or healthcare context.";
  if (graphMode === "source_provenance") return "Prioritizes source lineage so claims can be verified before briefing, escalation, or public communication.";
  return "Shows only the selected signal's direct evidence path so the relationship is readable and explainable.";
}

function intersectionReason(shared, evidenceShared) {
  if (!shared.length && evidenceShared) return "Shares the same evidence source or normalized feed lineage.";
  const kinds = [...new Set(shared.map((node) => nodeKindLabel(node.kind).toLowerCase()))];
  if (!kinds.length) return "Related by record-level context.";
  return `Shares ${kinds.slice(0, 3).join(", ")}${evidenceShared ? " and evidence source" : ""}.`;
}

function relationshipMeaning(record, intersections, neighbors) {
  const sourceCount = neighbors.filter((node) => node.kind === "source").length;
  const strongest = intersections[0];
  if (!strongest) return "This signal currently stands mostly on its own. Treat it as a focused triage item and verify source details before broad escalation.";
  return `${displayTitle(record)} connects to ${intersections.length} other visible records through ${neighbors.length} direct graph nodes and ${sourceCount} source path${sourceCount === 1 ? "" : "s"}. The strongest intersection is ${displayTitle(strongest.record)}, which is useful for deciding whether this is an isolated alert or part of a broader exposure pattern.`;
}

function sameEvidenceSource(record, selectedRecord) {
  if (!record || !selectedRecord) return false;
  return normalizeTerm(record.evidence?.source || record.source) === normalizeTerm(selectedRecord.evidence?.source || selectedRecord.source);
}

function sameRecordText(record, selectedRecord) {
  return sharedRecordTerms(record, selectedRecord).length > 0;
}

function sharedRecordTerms(record, selectedRecord) {
  const left = [
    ...(record.entities || []),
    ...(record.vulnerabilities || []),
    ...(record.observables || []),
    record.attributes?.vendor,
    record.attributes?.product,
    record.attributes?.package
  ].filter(Boolean);
  const right = new Set([
    ...(selectedRecord?.entities || []),
    ...(selectedRecord?.vulnerabilities || []),
    ...(selectedRecord?.observables || []),
    selectedRecord?.attributes?.vendor,
    selectedRecord?.attributes?.product,
    selectedRecord?.attributes?.package
  ].filter(Boolean).map(normalizeTerm));
  return left.filter((term) => right.has(normalizeTerm(term))).slice(0, 6);
}

function normalizeTerm(value) {
  return String(value || "").trim().toLowerCase();
}

function shortUrl(url) {
  try {
    const parsed = new URL(url);
    return `${parsed.hostname}${parsed.pathname}`.replace(/\/$/, "").slice(0, 58);
  } catch {
    return String(url).slice(0, 58);
  }
}

function GraphExplanation({ focus, graph, pinnedNodeId, graphMode }) {
  if (!focus) return null;
  const selected = focus.columns.find((column) => column.kind === "record")?.nodes[0];
  const pinned = graph.nodes.find((node) => node.id === pinnedNodeId);
  const counts = Object.fromEntries(focus.columns.map((column) => [column.kind, column.nodes.length]));
  const modeCopy = {
    selected_path: "Pinned to direct relationships for the selected signal. This is the cleanest provenance path for analyst triage.",
    all_related: "Shows the selected signal plus one related-neighborhood expansion so shared sources and context can be compared.",
    source_provenance: "Emphasizes source and evidence lineage first, useful before quoting or sharing a record externally."
  };
  return (
    <div className="graph-explain">
      <strong>{selected?.label || "Selected signal"}</strong>
      <p>{modeCopy[graphMode] || modeCopy.selected_path}{pinned ? ` Pinned node: ${pinned.label}.` : " Click any graph node to pin it."}</p>
      <div>
        <span>{counts.entity || 0} entities</span>
        <span>{counts.vulnerability || 0} CVEs</span>
        <span>{counts.observable || 0} IOCs</span>
        <span>{counts.source || 0} sources</span>
      </div>
    </div>
  );
}

function RelationshipFocus({ focus, records, onSelect }) {
  return (
    <div className="relationship-focus-strip" aria-label="Selected relationship path">
      {focus.columns.map((column) => (
        <div key={column.kind} className="relationship-focus-column">
          <span>{column.label}</span>
          <div>
            {column.nodes.length ? column.nodes.map((node) => {
              const record = records.find((item) => item.id === node.id);
              const clickable = Boolean(record);
              const Tag = clickable ? "button" : "article";
              return (
                <Tag key={node.id} className={cn("relationship-focus-node", node.id === focus.selectedId && "relationship-focus-node-active")} onClick={clickable ? () => onSelect(node.id) : undefined}>
                  <strong>{node.label}</strong>
                  <small>{node.kindLabel}</small>
                </Tag>
              );
            }) : <p>No direct node</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function SelectedSignalPanel({ record, records, copied, workflow, onCopy, onWorkflow }) {
  if (!record) return null;
  const actions = recommendedActions(record);
  const connected = relatedRecords(record, records);
  return (
    <aside className="selected-signal">
      <div className="selected-title">
        <Badge kind={record.kind}>{kindLabels[record.kind] || record.kind}</Badge>
        <strong>{impactScore(record)}/100</strong>
      </div>
      <h2>{displayTitle(record)}</h2>
      <p>{record.summary}</p>

      <div className="dossier-grid">
        <InfoBlock label="Who should care" value={whoCares(record)} />
        <InfoBlock label="Clinical impact" value={healthcareImpact(record)} />
        <InfoBlock label="Evidence posture" value={`${record.evidence?.claim_language || "unknown"} / ${record.evidence?.source || record.source}`} />
        <InfoBlock label="Connected signals" value={`${connected.length} direct record-level links`} />
      </div>

      <WorkflowControl value={workflow} onChange={onWorkflow} />
      <ScoreFactors record={record} compact />

      <div className="action-stack">
        <h3><ListChecks className="h-4 w-4" /> Action plan</h3>
        <TimelineStep label="24h" body={actions.now} />
        <TimelineStep label="7d" body={actions.week} />
        <TimelineStep label="30d" body={actions.month} />
      </div>

      <button className="btn btn-primary selected-copy" onClick={onCopy}>
        <Clipboard className="h-4 w-4" />
        {copied ? "Copied" : "Copy analyst brief"}
      </button>
    </aside>
  );
}

function ImpactCompass({ records, simulations }) {
  const items = [
    ["Clinical operations", records.filter((record) => /clinical|hospital|patient|EHR|PHI|billing|claims/i.test(`${record.summary} ${JSON.stringify(record.attributes || {})}`)).length, "Signals touching PHI, hospital workflows, claims, billing, or clinical operations."],
    ["Patient safety", records.filter((record) => record.kind === "medical_device_advisory").length + simulations.filter((scenario) => scenario.scorecard?.patient_safety_relevant).length, "Device advisories and simulations where cyber decisions can affect clinical continuity."],
    ["AI security", records.filter((record) => record.kind === "package_advisory").length + simulations.filter((scenario) => scenario.scorecard?.ai_security_relevant).length, "Repo, dependency, RAG, agent, token, and AI workflow exposure."],
    ["Evidence debt", evidenceDebtCount(records), "Records that require careful claim language before public use."]
  ];
  return (
    <section className="section-shell impact-compass-panel">
      <SectionHeader kicker="Impact compass" title="Where risk turns into operational work" />
      <div className="grid gap-3 md:grid-cols-4">
        {items.map(([label, value, body]) => (
          <article key={label} className="rounded-lg border border-slate-200 bg-white p-4">
            <b className="text-3xl font-black text-teal-700">{value}</b>
            <strong className="mt-4 block leading-tight">{label}</strong>
            <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function InsightPanel({ records, graph, simulations }) {
  return (
    <section className="section-shell insight-panel">
      <SectionHeader kicker="Connection engine" title="What the data is telling us" />
      <div className="grid gap-2">
        {buildInsights(records, graph, simulations).map(([title, body]) => (
          <article key={title} className="rounded-lg border border-l-4 border-slate-200 border-l-teal-600 bg-slate-50 p-4">
            <strong>{title}</strong>
            <p className="mt-1 text-sm leading-6 text-slate-600">{body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function DecisionQueue({ records, selectedId, onSelect }) {
  return (
    <section className="section-shell decision-queue-panel">
      <SectionHeader kicker="Decision queue" title="Top records by impact">
        <span className="text-sm text-slate-500">{records.length} shown</span>
      </SectionHeader>
      <div className="grid gap-2">
        {records.map((record) => (
          <button key={record.id} className={cn("record-button", selectedId === record.id && "record-button-active")} onClick={() => onSelect(record.id)}>
            <span className="min-w-0">
              <strong className="block truncate">{displayTitle(record)}</strong>
              <span className="text-sm text-slate-500">{displaySubtitle(record)} / {record.evidence?.claim_language || "unknown"}</span>
            </span>
            <Badge kind={record.kind}>{impactScore(record)}</Badge>
          </button>
        ))}
      </div>
    </section>
  );
}

function SectionHeader({ kicker, title, children }) {
  return (
    <div className="mb-5 grid gap-3 md:flex md:items-start md:justify-between">
      <div>
        <p className="kicker">{kicker}</p>
        <h2 className="text-2xl font-black leading-tight text-slate-950">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function FilterBar({ filterKind, onChange }) {
  return (
    <div className="filter-bar grid grid-cols-2 gap-2 sm:flex">
      {filters.map(([kind, label]) => (
        <button key={kind} className={cn("chip", filterKind === kind && "chip-active")} onClick={() => onChange(kind)}>
          <Filter className="h-3.5 w-3.5" />
          {label}
        </button>
      ))}
    </div>
  );
}

function SavedViews({ selectedView, onView, records }) {
  return (
    <div className="saved-view-strip">
      <button className={cn("saved-view", selectedView === "all" && "saved-view-active")} onClick={() => onView("all")}>
        <strong>All records</strong>
        <span>{records.length} total</span>
      </button>
      {savedViews.map(([id, label, body]) => {
        const count = records.filter((record) => matchesSavedView(record, id)).length;
        return (
          <button key={id} className={cn("saved-view", selectedView === id && "saved-view-active")} onClick={() => onView(id)}>
            <strong>{label}</strong>
            <span>{count} records</span>
            <small>{body}</small>
          </button>
        );
      })}
    </div>
  );
}

function AssetMatcher({ assetInput, matches, onAssetInput, onSelect }) {
  const assets = parseAssetInventory(assetInput);
  const topMatches = matches.slice(0, 12);
  async function loadFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    onAssetInput(await file.text());
  }
  return (
    <div className="asset-matcher">
      <section className="asset-input-panel">
        <div className="asset-panel-head">
          <div>
            <span>Inventory input</span>
            <strong>{assets.length} parsed assets</strong>
          </div>
          <label className="file-load-button">
            <FileUp className="h-4 w-4" />
            Upload
            <input accept=".csv,.txt,.json" type="file" onChange={loadFile} />
          </label>
        </div>
        <textarea
          value={assetInput}
          onChange={(event) => onAssetInput(event.target.value)}
          placeholder="Paste vendors, products, package names, domains, CVEs, or CSV rows..."
        />
        <p>Paste CSV, one asset per line, package names, products, vendors, domains, CVEs, or mixed notes. Inventory is stored locally in this browser.</p>
      </section>

      <section className="asset-results-panel">
        <div className="asset-panel-head">
          <div>
            <span>Matched exposure</span>
            <strong>{matches.length} affected intelligence records</strong>
          </div>
          <b>{topMatches[0]?.score || 0}/100 top match</b>
        </div>
        <div className="asset-match-list">
          {topMatches.length ? topMatches.map((match) => (
            <button key={match.record.id} onClick={() => onSelect(match.record.id)}>
              <span>
                <strong>{displayTitle(match.record)}</strong>
                <small>{match.matchedAssets.slice(0, 4).join(", ")}</small>
                <em>{match.reasons.join(" / ")}</em>
              </span>
              <b>{match.score}</b>
            </button>
          )) : (
            <article>
              <strong>No direct matches yet</strong>
              <p>Add products, vendors, packages, CVEs, domains, or medical-device inventory to see affected records.</p>
            </article>
          )}
        </div>
      </section>

      <aside className="asset-guidance-panel">
        <span>Matcher model</span>
        <strong>Exact identifiers first, fuzzy context second</strong>
        <p>CVEs, package names, vendor/product strings, domains, and entity names get the strongest match. Summaries and healthcare workflow keywords add context but do not override source evidence.</p>
        <div>
          <b>{matches.filter((match) => match.score >= 80).length}</b>
          <span>High-confidence hits</span>
        </div>
        <div>
          <b>{matches.filter((match) => /medical|patient|clinical|device/i.test(match.matchedAssets.join(" "))).length}</b>
          <span>Clinical/device hits</span>
        </div>
      </aside>
    </div>
  );
}

function BriefControls({ briefMode, briefTone, onMode, onTone, onCopy, copied }) {
  return (
    <div className="brief-controls">
      <label>
        <span>Mode</span>
        <select value={briefMode} onChange={(event) => onMode(event.target.value)}>
          {briefModes.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
      </label>
      <label>
        <span>Tone</span>
        <select value={briefTone} onChange={(event) => onTone(event.target.value)}>
          {briefTones.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
      </label>
      <button className="btn btn-dark" onClick={onCopy}>
        <Clipboard className="h-4 w-4" />
        {copied ? "Copied" : "Copy brief"}
      </button>
    </div>
  );
}

function WorkflowControl({ value, onChange }) {
  return (
    <div className="workflow-control">
      <div>
        <span>Analyst workflow</span>
        <strong>{value}</strong>
      </div>
      <div className="workflow-steps">
        {workflowSteps.map((stage) => (
          <button key={stage} className={cn(value === stage && "workflow-step-active")} onClick={() => onChange(stage)}>
            {stage}
          </button>
        ))}
      </div>
    </div>
  );
}

function ScoreFactors({ record, compact = false }) {
  const factors = scoreFactorDetails(record);
  return (
    <article className={cn("score-factors", compact && "score-factors-compact")}>
      <div className="score-factors-head">
        <span>Why this score</span>
        <strong>{impactScore(record)}/100</strong>
      </div>
      <div>
        {factors.map(([label, body, active]) => (
          <div key={label} className={cn(active && "score-factor-active")}>
            <b>{label}</b>
            <span>{body}</span>
          </div>
        ))}
      </div>
    </article>
  );
}

function EntityGraph({ graph, records, filterKind, selectedId, pinnedNodeId = "", onSelect, onNodePin, mode = "standard", graphMode = "selected_path" }) {
  const layout = useMemo(() => layoutGraph(graph.nodes, graph.edges, records, filterKind, selectedId, pinnedNodeId, mode, graphMode), [graph, records, filterKind, selectedId, pinnedNodeId, mode, graphMode]);
  return (
    <div className={cn("min-h-[500px] overflow-hidden rounded-lg border border-slate-200 bg-graph", mode === "workbench" && "relationship-canvas")}>
      <svg className="graph-svg h-[500px] w-full xl:h-[620px]" viewBox={`0 0 ${layout.width} ${layout.height}`} role="img" aria-label="Healthcare cyber entity graph">
        {layout.lanes.map((lane) => (
          <g key={lane.label}>
            <line x1={lane.x} y1="44" x2={lane.x} y2={layout.height - 34} className="graph-lane" />
            <text x={lane.x} y="24" textAnchor="middle" className="graph-lane-label">{lane.label}</text>
          </g>
        ))}
        {layout.edges.map((edge) => (
          <path
            key={`${edge.source}-${edge.target}-${edge.relation}`}
            d={`M ${edge.sourceNode.x} ${edge.sourceNode.y} C ${(edge.sourceNode.x + edge.targetNode.x) / 2} ${edge.sourceNode.y}, ${(edge.sourceNode.x + edge.targetNode.x) / 2} ${edge.targetNode.y}, ${edge.targetNode.x} ${edge.targetNode.y}`}
            className={cn("graph-edge", edge.active && "graph-edge-active")}
          />
        ))}
        {layout.nodes.map((node) => {
          const record = records.find((item) => item.id === node.id);
          const kind = record?.kind || node.kind;
          const active = selectedId === node.id;
          const pinned = pinnedNodeId === node.id;
          const contextual = layout.activeNodeIds.has(node.id);
          const label = graphLabelCallout(node, records, layout.width, mode);
          return (
            <Tooltip.Root key={node.id}>
              <Tooltip.Trigger asChild>
                <g className={cn("graph-node cursor-pointer", contextual && "graph-node-context", active && "graph-node-active", pinned && "graph-node-pinned")} onClick={() => {
                  onNodePin?.(node.id);
                  if (record) onSelect(record.id);
                }}>
                  <circle cx={node.x} cy={node.y} r={active ? 15 : node.kind === "record" ? 10 : 7} fill={kindColors[kind] || kindColors[node.kind] || "#143136"} />
                  {(active || contextual) && (
                    <g className="graph-label-callout pointer-events-none">
                      <rect x={label.x} y={label.y} width={label.width} height="24" rx="5" />
                      <text x={label.textX} y={label.y + 16}>{label.text}</text>
                    </g>
                  )}
                </g>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content className="rounded-lg bg-slate-950 px-3 py-2 text-xs text-white shadow-xl" sideOffset={8}>
                  {displayGraphLabel(node, records)}
                  <Tooltip.Arrow className="fill-slate-950" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          );
        })}
      </svg>
    </div>
  );
}

function graphLabelCallout(node, records, width, mode) {
  const maxChars = mode === "workbench" ? 22 : 28;
  const raw = displayGraphLabel(node, records);
  const text = raw.length > maxChars ? `${raw.slice(0, maxChars - 1)}...` : raw;
  const labelWidth = Math.min(mode === "workbench" ? 156 : 208, Math.max(64, text.length * 7 + 18));
  const labelGutter = 16;
  const placeLeft = node.x + labelWidth + labelGutter > width - 14;
  const x = placeLeft ? Math.max(8, node.x - labelWidth - labelGutter) : node.x + labelGutter;
  const y = Math.max(34, node.y - 32);
  return {
    text,
    width: labelWidth,
    x,
    y,
    textX: x + 8
  };
}

function layoutGraph(nodes, edges, records, filterKind, selectedId, pinnedNodeId = "", mode = "standard", graphMode = "selected_path") {
  const visibleRecordIds = new Set(records.filter((record) => filterKind === "all" || record.kind === filterKind).map((record) => record.id));
  const visibleNodeIds = new Set(visibleRecordIds);
  const activeNodeIds = new Set(selectedId ? [selectedId] : []);
  if (pinnedNodeId) activeNodeIds.add(pinnedNodeId);
  const activeEdgeIds = new Set();
  if (mode === "workbench" && selectedId) {
    visibleNodeIds.clear();
    visibleNodeIds.add(selectedId);
  }
  edges.forEach((edge) => {
    const touchesVisibleRecord = visibleRecordIds.has(edge.source) || visibleRecordIds.has(edge.target);
    const touchesSelected = selectedId && (edge.source === selectedId || edge.target === selectedId);
    if (touchesVisibleRecord && mode !== "workbench") {
      visibleNodeIds.add(edge.source);
      visibleNodeIds.add(edge.target);
    }
    if (touchesSelected) {
      activeNodeIds.add(edge.source);
      activeNodeIds.add(edge.target);
      visibleNodeIds.add(edge.source);
      visibleNodeIds.add(edge.target);
      activeEdgeIds.add(edge.id);
    }
    if (pinnedNodeId && (edge.source === pinnedNodeId || edge.target === pinnedNodeId)) {
      visibleNodeIds.add(edge.source);
      visibleNodeIds.add(edge.target);
      activeNodeIds.add(edge.source);
      activeNodeIds.add(edge.target);
      activeEdgeIds.add(edge.id);
    }
  });
  if (mode === "workbench" && selectedId && graphMode !== "selected_path") {
    const seedIds = new Set(visibleNodeIds);
    edges.forEach((edge) => {
      const sourceNode = nodes.find((node) => node.id === edge.source);
      const targetNode = nodes.find((node) => node.id === edge.target);
      const sourceIsRecord = sourceNode?.kind === "record";
      const targetIsRecord = targetNode?.kind === "record";
      const sourceIsSource = sourceNode?.kind === "source";
      const targetIsSource = targetNode?.kind === "source";
      const touchesSeed = seedIds.has(edge.source) || seedIds.has(edge.target);
      const allowedExpansion = graphMode === "all_related" || (graphMode === "source_provenance" && (sourceIsSource || targetIsSource || sourceIsRecord || targetIsRecord));
      if (touchesSeed && allowedExpansion) {
        visibleNodeIds.add(edge.source);
        visibleNodeIds.add(edge.target);
        if (activeNodeIds.has(edge.source) || activeNodeIds.has(edge.target)) activeEdgeIds.add(edge.id);
      }
    });
  }
  if (!selectedId) {
    edges.forEach((edge) => {
      if (visibleRecordIds.has(edge.source) || visibleRecordIds.has(edge.target)) {
        activeEdgeIds.add(edge.id);
      }
    });
  }
  const visible = nodes.filter((node) => visibleNodeIds.has(node.id));
  if (mode === "workbench" && selectedId && !visible.some((node) => node.id === selectedId)) {
    const selectedNode = nodes.find((node) => node.id === selectedId);
    if (selectedNode) visible.push(selectedNode);
  }
  const columns = ["record", "entity", "vulnerability", "observable", "source"];
  const laneLabels = {
    record: "Signals",
    entity: "Entities",
    vulnerability: "CVEs",
    observable: "IOCs",
    source: "Sources"
  };
  const grouped = Object.fromEntries(columns.map((kind) => [kind, []]));
  visible.forEach((node) => {
    grouped[grouped[node.kind] ? node.kind : "record"].push(node);
  });
  const width = mode === "workbench" ? 760 : 1000;
  const height = mode === "workbench" ? 410 : 620;
  const x = d3.scalePoint(columns, mode === "workbench" ? [80, 700] : [104, 910]);
  const positioned = new Map();
  Object.entries(grouped).forEach(([kind, group]) => {
    const sortedGroup = [...group].sort((a, b) => {
      const aActive = activeNodeIds.has(a.id) ? -1 : 0;
      const bActive = activeNodeIds.has(b.id) ? -1 : 0;
      return aActive - bActive || a.label.localeCompare(b.label);
    });
    const yRange = sortedGroup.length <= 3 ? [120, height - 130] : [78, height - 54];
    const y = d3.scalePoint(sortedGroup.map((node) => node.id), yRange);
    sortedGroup.forEach((node) => positioned.set(node.id, { ...node, x: x(kind) || 160, y: y(node.id) || height / 2 }));
  });
  return {
    width,
    height,
    lanes: columns.map((kind) => ({ kind, label: laneLabels[kind], x: x(kind) || 0 })),
    activeNodeIds,
    nodes: [...positioned.values()],
    edges: edges
      .filter((edge) => positioned.has(edge.source) && positioned.has(edge.target))
      .map((edge) => ({
        ...edge,
        active: activeEdgeIds.has(edge.id),
        sourceNode: positioned.get(edge.source),
        targetNode: positioned.get(edge.target)
      }))
  };
}

function relationshipFocus(graph, records, selectedId) {
  if (!selectedId) return null;
  const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));
  const selectedNode = nodeById.get(selectedId);
  if (!selectedNode) return null;
  const activeIds = new Set([selectedId]);
  graph.edges.forEach((edge) => {
    if (edge.source === selectedId || edge.target === selectedId) {
      activeIds.add(edge.source);
      activeIds.add(edge.target);
    }
  });
  const columns = ["record", "entity", "vulnerability", "observable", "source"];
  const laneLabels = {
    record: "Signals",
    entity: "Entities",
    vulnerability: "CVEs",
    observable: "IOCs",
    source: "Sources"
  };
  const activeNodes = [...activeIds].map((id) => nodeById.get(id)).filter(Boolean);
  return {
    selectedId,
    columns: columns.map((kind) => ({
      kind,
      label: laneLabels[kind],
      nodes: activeNodes
        .filter((node) => node.kind === kind || (kind === "record" && !columns.includes(node.kind)))
        .slice(0, 3)
        .map((node) => {
          const record = records.find((item) => item.id === node.id);
          return {
            id: node.id,
            label: record ? displayTitle(record) : node.label,
            kindLabel: record ? kindLabels[record.kind] || record.kind : laneLabels[node.kind] || node.kind
          };
        })
    }))
  };
}

function displayGraphLabel(node, records) {
  const record = records.find((item) => item.id === node.id);
  if (record) return displayTitle(record);
  return node.label;
}

function matchesSavedView(record, view) {
  const text = `${displayTitle(record)} ${record.summary} ${record.kind} ${(record.entities || []).join(" ")} ${JSON.stringify(record.attributes || {})}`.toLowerCase();
  const attrs = record.attributes || {};
  if (view === "critical_cves") {
    return record.kind === "vulnerability" && (attrs.kev || Number(attrs.cvss) >= 9 || Number.parseFloat(attrs.epss || 0) >= 0.9 || /remote|file transfer|identity|gateway|medical|hospital/.test(text));
  }
  if (view === "device_risk") return record.kind === "medical_device_advisory" || /medical device|patient monitor|gateway|clinical device|patient safety/.test(text);
  if (view === "breach_claims") return ["breach", "ransomware_claim"].includes(record.kind) || /ocr|breach|ransomware|attacker-claimed|phi/.test(text);
  if (view === "ai_supply_chain") return record.kind === "package_advisory" || /ai|rag|agent|token|dependency|package|repo|supply-chain|secret/.test(text);
  if (view === "evidence_debt") return record.evidence?.claim_language !== "source-confirmed" || /fixture|demo|attacker-claimed|simulation/.test(text);
  if (view === "patient_safety") return /patient|clinical|hospital|device|ehr|phi|claims|billing|care|safety/.test(text);
  return true;
}

function generateConfiguredBrief(record, records, mode, tone) {
  const modeLabel = briefModes.find(([id]) => id === mode)?.[1] || "Technical triage";
  const toneLabel = briefTones.find(([id]) => id === tone)?.[1] || "Cautious";
  const factors = scoreFactorDetails(record).filter(([, , active]) => active).map(([label, body]) => `- ${label}: ${body}`).join("\n");
  return `${modeLabel} / ${toneLabel}

Use the following brief as evidence-aware healthcare cyber intelligence. Keep uncertainty visible, avoid overstating fixture/demo or attacker-claimed data, and make the action language appropriate for ${modeLabel.toLowerCase()}.

Score rationale:
${factors || "- No high-confidence scoring factors found beyond baseline record classification."}

${generateBrief(record, records)}`;
}

function scoreFactorDetails(record) {
  const attrs = record.attributes || {};
  const summary = record.summary || "";
  const text = `${displayTitle(record)} ${summary} ${JSON.stringify(attrs)}`.toLowerCase();
  return [
    ["KEV listed", "Known Exploited Vulnerability status increases patch urgency.", Boolean(attrs.kev)],
    ["High CVSS", "Technical severity is high enough to warrant rapid triage.", Number(attrs.cvss) >= 9],
    ["High EPSS", "Exploit likelihood is materially elevated.", Number.parseFloat(attrs.epss || 0) >= 0.9],
    ["Healthcare-adjacent asset", "Record touches clinical, hospital, identity, file transfer, PHI, or device workflows.", /patient|clinical|medical|hospital|phi|ehr|identity|file transfer|gateway|device/.test(text)],
    ["Patient-safety relevance", "Signal may affect clinical continuity, device operations, or care delivery.", /patient safety|patient monitor|clinical|care delivery|device|hospital/.test(text)],
    ["Evidence quality risk", "Claim language requires careful validation before external use.", record.evidence?.claim_language !== "source-confirmed"]
  ];
}

function liveDataReadiness(records) {
  const retrieved = records.map((record) => record.evidence?.retrieved_at || record.created_at).filter(Boolean).sort();
  const latest = retrieved[retrieved.length - 1] || "unknown";
  const fixtureCount = records.filter((record) => /fixture|demo|sample/i.test(`${record.source} ${record.evidence?.source || ""}`)).length;
  const liveCount = records.length - fixtureCount;
  const careful = evidenceDebtCount(records);
  return {
    status: liveCount ? "Live feeds active with fixture context retained" : "Ready for live feeds, currently using controlled fixture data",
    summary: liveCount
      ? "Machine-readable live sources are normalized beside controlled healthcare fixtures, with source freshness and evidence caveats visible before action."
      : "The interface exposes source freshness, fixture/demo language, evidence debt, and ingest state before users act on a signal.",
    items: [
      ["Records", records.length],
      ["Live records", liveCount],
      ["Latest retrieval", latest.replace("T", " ").slice(0, 16)],
      ["Fixture/demo", fixtureCount],
      ["Evidence debt", careful]
    ]
  };
}

function parseAssetInventory(input) {
  return input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !/^vendor,product,type,owner$/i.test(line))
    .flatMap((line) => {
      if (line.startsWith("{")) {
        try {
          const obj = JSON.parse(line);
          return Object.values(obj).map((value) => String(value));
        } catch {
          return [line];
        }
      }
      return line.split(/[,;\t|]/).map((part) => part.trim()).filter(Boolean);
    })
    .map((value) => value.replace(/^["']|["']$/g, "").trim())
    .filter((value) => value.length > 2)
    .filter((value, index, all) => all.findIndex((item) => item.toLowerCase() === value.toLowerCase()) === index);
}

function matchAssetsToRecords(input, records) {
  const assets = parseAssetInventory(input);
  if (!assets.length) return [];
  return records
    .map((record) => assetRecordMatch(record, assets))
    .filter((match) => match.score > 0)
    .sort((a, b) => b.score - a.score || impactScore(b.record) - impactScore(a.record));
}

function assetRecordMatch(record, assets) {
  const attrs = record.attributes || {};
  const haystackParts = [
    displayTitle(record),
    displaySubtitle(record),
    record.summary,
    record.kind,
    ...(record.entities || []),
    ...(record.vulnerabilities || []),
    ...(record.observables || []),
    attrs.vendor,
    attrs.product,
    attrs.package,
    attrs.identifier,
    attrs.cve
  ].filter(Boolean);
  const exactText = haystackParts.join(" ").toLowerCase();
  const summaryText = `${record.summary || ""} ${JSON.stringify(attrs)}`.toLowerCase();
  const matchedAssets = [];
  const reasons = new Set();
  let score = 0;
  for (const asset of assets) {
    const term = asset.toLowerCase();
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const exact = new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, "i");
    if (exact.test(exactText)) {
      matchedAssets.push(asset);
      score += /^CVE-\d{4}-\d+$/i.test(asset) ? 55 : 38;
      reasons.add(/^CVE-/i.test(asset) ? "CVE match" : "asset/entity match");
    } else if (term.length >= 5 && summaryText.includes(term)) {
      matchedAssets.push(asset);
      score += 18;
      reasons.add("context match");
    }
  }
  if (matchedAssets.length) {
    score += Math.min(20, impactScore(record) / 5);
    if (record.attributes?.kev) {
      score += 12;
      reasons.add("KEV priority");
    }
    if (/patient|clinical|medical|hospital|device|phi|ehr/i.test(`${matchedAssets.join(" ")} ${record.summary}`)) {
      score += 10;
      reasons.add("healthcare workflow");
    }
  }
  return {
    record,
    score: Math.min(100, Math.round(score)),
    matchedAssets: [...new Set(matchedAssets)].slice(0, 8),
    reasons: [...reasons]
  };
}

function ConnectionPanel({ selected, records }) {
  if (!selected) return null;
  const connected = relatedRecords(selected, records);
  return (
    <aside className="rounded-lg border border-slate-200 bg-white p-5">
      <Badge kind={selected.kind}>{kindLabels[selected.kind] || selected.kind}</Badge>
      <h3 className="mt-4 text-lg font-black">{selected.title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{healthcareImpact(selected)}</p>
      <div className="mt-4 grid gap-2 text-sm">
        <InfoLine label="Impact score" value={`${impactScore(selected)}/100`} />
        <InfoLine label="Evidence" value={selected.evidence?.claim_language || "unknown"} />
        <InfoLine label="Who cares" value={whoCares(selected)} />
        <InfoLine label="Connected" value={`${connected.length} records`} />
      </div>
    </aside>
  );
}

function ImpactTranslator({ record, records, workflow, onWorkflow }) {
  const actions = recommendedActions(record);
  const connected = relatedRecords(record, records);
  return (
    <div className="impact-workbench">
      <article className="impact-focus">
        <div>
          <Badge kind={record.kind}>{kindLabels[record.kind] || record.kind}</Badge>
          <h3>{displayTitle(record)}</h3>
          <p>{record.summary}</p>
        </div>
        <div className="impact-scorebox">
          <span>Impact score</span>
          <strong>{impactScore(record)}</strong>
          <small>{record.evidence?.claim_language || "unknown"}</small>
        </div>
      </article>

      <div className="impact-matrix">
        <InfoBlock label="Who should care" value={whoCares(record)} />
        <InfoBlock label="Clinical and operational impact" value={healthcareImpact(record)} />
        <InfoBlock label="Evidence posture" value={`${record.evidence?.claim_language || "unknown"} from ${record.evidence?.source || record.source}`} />
        <InfoBlock label="Connected signals" value={`${connected.length} direct record-level links`} />
      </div>

      <div className="impact-secondary">
        <WorkflowControl value={workflow} onChange={onWorkflow} />
        <ScoreFactors record={record} />
      </div>

      <div className="impact-secondary">
        <article className="action-matrix">
          <h3>Action timeline</h3>
          <TimelineStep label="24h" body={actions.now} />
          <TimelineStep label="7d" body={actions.week} />
          <TimelineStep label="30d" body={actions.month} />
        </article>
        <article className="connected-card">
          <h3>Connected evidence</h3>
          <div className="connected-list">
            {connected.length ? connected.map((item) => (
              <div key={item.id}>
                <strong>{displayTitle(item)}</strong>
                <span>{kindLabels[item.kind] || item.kind} / {item.evidence?.claim_language || "unknown"}</span>
              </div>
            )) : <p>No direct record-level links in current normalized data.</p>}
          </div>
        </article>
      </div>
    </div>
  );
}

function RecordsTable({ records, selectedId, workflow, onWorkflow, onSelect }) {
  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Record",
        cell: ({ row }) => (
          <div>
            <strong>{displayTitle(row.original)}</strong>
            <p className="mt-1 line-clamp-2 text-sm leading-5 text-slate-500">{row.original.summary}</p>
          </div>
        )
      },
      {
        accessorKey: "kind",
        header: "Kind",
        cell: ({ row }) => <Badge kind={row.original.kind}>{kindLabels[row.original.kind] || row.original.kind}</Badge>
      },
      {
        id: "impact",
        header: "Score",
        cell: ({ row }) => <strong>{impactScore(row.original)}</strong>
      },
      {
        id: "evidence",
        header: "Evidence",
        cell: ({ row }) => <span className="text-sm text-slate-600">{row.original.evidence?.claim_language || "unknown"}</span>
      },
      {
        id: "workflow",
        header: "Workflow",
        cell: ({ row }) => (
          <select
            className="workflow-select"
            value={workflow[row.original.id] || workflowSteps[0]}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.stopPropagation();
              onWorkflow(row.original.id, event.target.value);
            }}
          >
            {workflowSteps.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
          </select>
        )
      },
      {
        id: "entities",
        header: "Entities",
        cell: ({ row }) => <span className="text-sm text-slate-600">{(row.original.entities || []).slice(0, 3).join(", ") || "none"}</span>
      },
      {
        id: "source",
        header: "Source",
        cell: ({ row }) => <span className="text-sm text-slate-600">{row.original.evidence?.source || row.original.source}</span>
      }
    ],
    [onWorkflow, workflow]
  );
  const table = useReactTable({ data: records, columns, getCoreRowModel: getCoreRowModel() });
  return (
    <div className="overflow-auto rounded-lg border border-slate-200 bg-white">
      <table className="min-w-[980px] w-full border-collapse">
        <thead className="bg-slate-50">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="border-b border-slate-200 px-4 py-3 text-left text-xs font-black uppercase tracking-[0.12em] text-slate-500">
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className={cn("cursor-pointer transition hover:bg-slate-50", selectedId === row.original.id && "bg-teal-50/70")} onClick={() => onSelect(row.original.id)}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="border-b border-slate-100 px-4 py-3 align-top">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecordDetail({ record, records, workflow, onWorkflow }) {
  if (!record) return null;
  const connected = relatedRecords(record, records);
  const attrs = Object.entries(record.attributes || {}).slice(0, 12);
  const refs = (record.references || []).slice(0, 5);
  return (
    <aside className="record-dossier">
      <div className="record-dossier-head">
        <Badge kind={record.kind}>{kindLabels[record.kind] || record.kind}</Badge>
        <strong>{impactScore(record)}/100</strong>
      </div>
      <h3>{displayTitle(record)}</h3>
      <p>{record.summary}</p>
      <WorkflowControl value={workflow} onChange={onWorkflow} />
      <ScoreFactors record={record} compact />
      <div className="dossier-section">
        <h4>Source posture</h4>
        <InfoLine label="Evidence" value={record.evidence?.claim_language || "unknown"} />
        <InfoLine label="Source" value={record.evidence?.source || record.source} />
        <InfoLine label="Retrieved" value={record.evidence?.retrieved_at || record.created_at || "unknown"} />
      </div>
      <div className="dossier-section">
        <h4>Entities and attributes</h4>
        <InfoLine label="Entities" value={(record.entities || []).join(", ") || "none"} />
        <InfoLine label="Vulnerabilities" value={(record.vulnerabilities || []).join(", ") || "none"} />
        <div className="attribute-grid">
          {attrs.length ? attrs.map(([key, value]) => (
            <div key={key}>
              <span>{key}</span>
              <strong>{Array.isArray(value) ? value.join(", ") : String(value)}</strong>
            </div>
          )) : <p>No additional structured attributes.</p>}
        </div>
      </div>
      <div className="dossier-section">
        <h4>References and links</h4>
        <div className="reference-list">
          {refs.length ? refs.map((ref) => <a key={ref} href={ref} target="_blank" rel="noreferrer">{ref}</a>) : <span>none</span>}
        </div>
      </div>
      <div className="dossier-section">
        <h4>Connected records</h4>
        <InfoLine label="Connected" value={connected.length ? connected.map((item) => displayTitle(item)).join(", ") : "none"} />
      </div>
    </aside>
  );
}

function EvidenceStats({ records, filtered }) {
  const verified = records.filter((record) => record.evidence?.claim_language === "source-confirmed").length;
  const attackerClaimed = records.filter((record) => record.evidence?.claim_language === "attacker-claimed").length;
  return (
    <div className="evidence-stats">
      <div><span>Visible</span><strong>{filtered.length}</strong></div>
      <div><span>Total records</span><strong>{records.length}</strong></div>
      <div><span>Evidence debt</span><strong>{evidenceDebtCount(records)}</strong></div>
      <div><span>Attacker claimed</span><strong>{attackerClaimed}</strong></div>
      <div><span>Source confirmed</span><strong>{verified}</strong></div>
    </div>
  );
}

function HelpSection() {
  const items = [
    ["Defenders", "Prioritize what to patch, hunt, block, simulate, or escalate in healthcare-specific terms."],
    ["Clinical leaders", "Understand patient-safety, downtime, vendor, and notification implications without cyber jargon overload."],
    ["Builders", "See where AI repos, RAG tools, package risk, and secrets intersect with PHI and clinical workflows."],
    ["Students and analysts", "Learn source discipline: what is confirmed, what is attacker-claimed, and what is only simulation."]
  ];
  return (
    <section id="help" className="section-shell">
      <SectionHeader kicker="Public utility" title="How this helps others" />
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {items.map(([title, body]) => (
          <article key={title} className="rounded-lg border border-slate-200 bg-white p-5">
            <h3 className="font-black">{title}</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function Badge({ kind, children }) {
  return <span className={cn("badge", kind && `badge-${kind}`)}>{children}</span>;
}

function InfoBlock({ label, value }) {
  return (
    <div className="border-t border-slate-200 pt-3">
      <strong className="block text-sm">{label}</strong>
      <span className="mt-1 block text-sm leading-6 text-slate-600">{value}</span>
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <div className="info-line rounded-lg border border-slate-200 bg-slate-50 p-3">
      <strong>{label}:</strong> <span className="text-slate-600">{value}</span>
    </div>
  );
}

function TimelineStep({ label, body }) {
  return (
    <div className="grid grid-cols-[54px_1fr] gap-3 border-b border-slate-200 pb-4 last:border-b-0 last:pb-0">
      <strong className="text-teal-700">{label}</strong>
      <p className="m-0 text-sm leading-6 text-slate-600">{body}</p>
    </div>
  );
}

createRoot(document.getElementById("root")).render(<App />);
